import type {
  IDraggablePanelPosition, IDraggablePanelSize,
} from '../interfaces/IDraggablePanel';

/** Smallest the panel stays usable at: a readable header and a line or two of content. */
export const MIN_PANEL_SIZE: IDraggablePanelSize = { width: 320, height: 240 };

/**
 * Holds the whole panel inside the viewport, so it can never be dragged out of reach.
 *
 * A panel wider or taller than the viewport is pinned to the top left rather than pushed off the
 * other edge, which keeps its header — and so its close button — reachable.
 */
export const clampPanelPosition = (
  position: IDraggablePanelPosition,
  size: IDraggablePanelSize,
  viewport: IDraggablePanelSize,
): IDraggablePanelPosition => ({
  x: Math.max(0, Math.min(position.x, viewport.width - size.width)),
  y: Math.max(0, Math.min(position.y, viewport.height - size.height)),
});

/**
 * Holds a resize between the size the panel is usable at and what is left of the viewport.
 *
 * The panel grows from its top left corner, so the room it has is what lies right and below of
 * where it sits. A `maxSize` from the caller narrows that further, and a `minSize` takes the place
 * of `MIN_PANEL_SIZE` — a caller that knows its own content may shrink further than the default.
 *
 * The floor wins over the ceiling: a panel in a viewport with less room than that keeps its
 * smallest usable size and overflows rather than collapsing.
 */
export const clampPanelSize = (
  size: IDraggablePanelSize,
  position: IDraggablePanelPosition,
  viewport: IDraggablePanelSize,
  maxSize?: IDraggablePanelSize,
  minSize?: IDraggablePanelSize,
): IDraggablePanelSize => {
  const minimum = minSize ?? MIN_PANEL_SIZE;
  const maxWidth = Math.min(maxSize?.width ?? Infinity, viewport.width - position.x);
  const maxHeight = Math.min(maxSize?.height ?? Infinity, viewport.height - position.y);

  return {
    width: Math.max(minimum.width, Math.min(size.width, maxWidth)),
    height: Math.max(minimum.height, Math.min(size.height, maxHeight)),
  };
};

export default clampPanelPosition;
